import { useState } from 'react';
import { AlertTriangle, Check, CircleDashed, Coins, HelpCircle, PlugZap } from 'lucide-react';
import { Button } from '@/components/Button';
import { Pill } from '@/components/StatusPill';
import { ErrorState, Skeleton } from '@/components/States';
import {
  useIntegrations,
  useTestFreeIntegrations,
  useTestIntegration,
} from '@/hooks/use-integrations';
import { describeError } from '@/lib/describe-error';
import type { IntegrationSummary, IntegrationTestResult } from '@/lib/integrations-api';
import { Roles, type Session } from '@/auth/keycloak';
import { cn } from '@/lib/utils';

/**
 * Integrations — what this deployment is wired to, and whether it works (spec § 4.4).
 *
 * ── WHY THIS SCREEN EXISTS ────────────────────────────────────────────────────────
 *
 * Because "configured" and "working" were the same word here until now. Every provider could report
 * that somebody had supplied a non-empty string, and nothing could tell a typo from a live key, a
 * revoked key from a valid one, or a SendGrid key carrying `mail.send` from one missing it. All three
 * of those otherwise surface for the first time when a real campaign tries to go out.
 *
 * It answers two questions with no other answer. Locally: did the key I just put in `.env` reach the
 * process at all — which is genuinely not obvious in the container stack. Deployed: did the Key Vault
 * sync work, which is the harder half, because a secret that failed to sync looks exactly like one
 * that succeeded until something uses it.
 *
 * ── IT SHOWS FOUR CHARACTERS OF A KEY, ON PURPOSE ─────────────────────────────────
 *
 * The question people have is not "is a key set" but "is it the RIGHT key" — a real question with
 * three narrow SendGrid keys in play and a rotation to get through. Four trailing characters tells two
 * keys apart and is worth nothing to anybody; the server refuses to fingerprint a value short enough
 * that four characters would be a meaningful share of it.
 *
 * There is no field to type a key into, and no endpoint behind one. A secret typed into a browser is a
 * secret in a request log, the browser's form history and possibly a password manager.
 */
export function IntegrationsPage({ session }: { session: Session }) {
  const isAdministrator = session.roles.includes(Roles.Admin);

  const { data, isPending, isError, error, refetch } = useIntegrations();

  /**
   * Results by key, in the page rather than in the query cache.
   *
   * A test result is a statement about one moment. Cached, it would say "valid" long after a key was
   * rotated — and somebody would believe it, which is worse than showing nothing.
   */
  const [results, setResults] = useState<Record<string, IntegrationTestResult>>({});
  const [sweepSkipped, setSweepSkipped] = useState<string[]>([]);

  const test = useTestIntegration((key, result) =>
    setResults((current) => ({ ...current, [key]: result })),
  );

  const sweep = useTestFreeIntegrations((outcome) => {
    setResults((current) => ({
      ...current,
      ...Object.fromEntries(outcome.results.map((result) => [result.key, result])),
    }));

    setSweepSkipped(outcome.skipped);
  });

  if (!isAdministrator) {
    return (
      <div className="px-4 py-6 sm:px-6">
        <h1 className="text-xl font-semibold text-heading">Integrations</h1>

        <p className="mt-2 max-w-2xl text-sm text-muted">
          Only an administrator can see this. It lists which external services this deployment is
          connected to, and part of each credential — ask an administrator if you need to know
          whether something is set up.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl px-4 py-6 sm:px-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-heading">Integrations</h1>

          <p className="mt-1 max-w-2xl text-sm text-muted">
            Everything this deployment depends on. <strong>Configured</strong> means a credential is
            present; it does not mean it works. Testing is the only way to know that — and it is worth
            doing after any deploy that changed a secret.
          </p>
        </div>

        {data !== undefined && (
          <Button
            variant="secondary"
            size="sm"
            isBusy={sweep.isPending}
            onClick={() => sweep.mutate()}
          >
            <PlugZap className="size-3.5" aria-hidden="true" />
            {sweep.isPending ? 'Checking…' : 'Test all free checks'}
          </Button>
        )}
      </div>

      <p className="mt-3 max-w-2xl rounded-md bg-surface-2 px-3 py-2 text-xs text-muted">
        Credentials come from the environment — <code className="text-foreground">.env</code> locally,
        Key Vault when deployed — and cannot be set from here. That is deliberate: a secret typed into
        a browser ends up in a request log and the browser&rsquo;s own history.
      </p>

      {isPending ? (
        <div className="mt-5 flex flex-col gap-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : isError ? (
        <div className="mt-5">
          <ErrorState error={error} onRetry={() => void refetch()} />
        </div>
      ) : (
        <>
          {sweep.isError && (
            <p role="alert" className="mt-4 rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-ink">
              {describeError(sweep.error).message}
            </p>
          )}

          {sweepSkipped.length > 0 && (
            <p role="status" className="mt-4 rounded-md bg-surface-2 px-3 py-2 text-sm text-muted">
              {sweepSkipped.length === 1
                ? '1 check was skipped because it costs money'
                : `${sweepSkipped.length} checks were skipped because they cost money`}
              . Test those individually — each one says what it will cost.
            </p>
          )}

          <ul className="mt-5 flex flex-col gap-3">
            {data.map((integration) => (
              <IntegrationRow
                key={integration.key}
                integration={integration}
                result={results[integration.key]}
                isTesting={test.isPending && test.variables === integration.key}
                error={
                  test.isError && test.variables === integration.key
                    ? describeError(test.error).message
                    : undefined
                }
                onTest={() => test.mutate(integration.key)}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function IntegrationRow({
  integration,
  result,
  isTesting,
  error,
  onTest,
}: {
  integration: IntegrationSummary;
  result: IntegrationTestResult | undefined;
  isTesting: boolean;
  error: string | undefined;
  onTest: () => void;
}) {
  return (
    <li className="rounded-lg border border-border bg-surface p-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold text-heading">{integration.label}</h2>

            {/* Never colour alone — the pill carries the word too (ui-ux.md § 7). */}
            {integration.isConfigured ? (
              <Pill tone="info">Configured</Pill>
            ) : (
              <Pill tone="neutral">Not configured</Pill>
            )}

            {integration.driver !== null && (
              <span className="font-mono text-xs text-muted">{integration.driver}</span>
            )}

            {integration.credentialFingerprint !== null && (
              <span
                className="font-mono text-xs text-muted"
                // Spelt out, because "…1234" is not self-explanatory and the alternative is
                // somebody assuming it is a truncated display of the whole value.
                title="The last four characters of the credential in use, so you can tell two keys apart"
              >
                key {integration.credentialFingerprint}
              </span>
            )}
          </div>

          <p className="mt-1 text-sm text-muted">{integration.consequence}</p>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            isBusy={isTesting}
            disabled={!integration.isConfigured}
            onClick={onTest}
          >
            {isTesting ? 'Testing…' : 'Test'}
          </Button>

          {/* The cost is stated BEFORE the button is pressed. */}
          {integration.testCostsMoney && (
            <span className="flex items-center gap-1 text-xs text-muted">
              <Coins className="size-3" aria-hidden="true" />
              Costs a little
            </span>
          )}
        </div>
      </div>

      {error !== undefined && (
        <p role="alert" className="mt-3 rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-ink">
          {error}
        </p>
      )}

      {result !== undefined && <Result result={result} />}
    </li>
  );
}

/**
 * One test's outcome.
 *
 * Three states rather than two, because "not configured" is neither a pass nor a failure — for an
 * optional dependency it is a decision, and marking it red would put a fault on a deployment that is
 * working exactly as intended.
 */
function Result({ result }: { result: IntegrationTestResult }) {
  const tone = !result.wasRun
    ? 'bg-surface-2 text-muted'
    : result.isHealthy
      ? 'bg-success-bg text-success-ink'
      : 'bg-danger-bg text-danger-ink';

  const Icon = !result.wasRun ? CircleDashed : result.isHealthy ? Check : AlertTriangle;

  return (
    <div role="status" aria-live="polite" className={cn('mt-3 rounded-md px-3 py-2 text-sm', tone)}>
      <p className="flex items-start gap-2">
        <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />

        <span className="min-w-0 flex-1">
          {/* The word as well as the colour and the icon. */}
          <strong>
            {!result.wasRun ? 'Not tested' : result.isHealthy ? 'Working' : 'Not working'}
          </strong>{' '}
          — {result.detail}
        </span>
      </p>

      {/* Often the useful half: "valid" and "valid, and it carries mail.send" answer different
          questions, and only the second one tells you a send will succeed. */}
      {result.extra !== null && (
        <p className="mt-1 pl-6 font-mono text-xs opacity-80">{result.extra}</p>
      )}

      {result.cost !== null && (
        <p className="mt-1 flex items-start gap-1.5 pl-6 text-xs opacity-80">
          <HelpCircle className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
          {result.cost}
        </p>
      )}
    </div>
  );
}
